/**
 * @fileoverview Day view — single-day time grid + side panel.
 *
 * Adapted from upstream:
 *  - DnD/dialog wrappers removed.
 *  - Bundled `SingleCalendar` (react-day-picker v8 fork) swapped for
 *    Morgan's app-wide `Calendar` (react-day-picker v9 wrapper).
 *  - Empty quarter-hour slots fire `onDateCellClick` so the page-level
 *    "create key date" panel opens with the time pre-filled.
 */

import { Briefcase, Calendar as CalendarIcon, Clock } from "lucide-react";
import { parseISO, areIntervalsOverlapping, format } from "date-fns";

import { useCalendar } from "@/calendar/contexts/use-calendar";

import { ScrollArea } from "@/ui/primitives/scroll-area";
import { Calendar } from "@/ui/composed/data-display/calendar";

import { EventBlock } from "@/calendar/components/week-and-day-view/event-block";
import { CalendarTimeline } from "@/calendar/components/week-and-day-view/calendar-time-line";
import { DayViewMultiDayEventsRow } from "@/calendar/components/week-and-day-view/day-view-multi-day-events-row";
import {
  useTimeGridSelection,
  formatMinLabel,
} from "@/calendar/components/week-and-day-view/use-time-grid-selection";

const HOUR_HEIGHT_PX = 96;

import { cn } from "@/utils/misc";
import {
  groupEvents,
  getEventBlockStyle,
  isWorkingHour,
  getCurrentEvents,
  getVisibleHours,
} from "@/calendar/helpers";

import type { IEvent } from "@/calendar/interfaces";

interface IProps {
  singleDayEvents: IEvent[];
  multiDayEvents: IEvent[];
}

export function CalendarDayView({ singleDayEvents, multiDayEvents }: IProps) {
  const {
    selectedDate,
    setSelectedDate,
    visibleHours,
    workingHours,
    onDateCellClick,
  } = useCalendar();

  const { hours, earliestEventHour, latestEventHour } = getVisibleHours(
    visibleHours,
    singleDayEvents,
  );

  const currentEvents = getCurrentEvents(singleDayEvents);

  const dayEvents = singleDayEvents.filter((event) => {
    const eventDate = parseISO(event.startDate);
    return (
      eventDate.getDate() === selectedDate.getDate() &&
      eventDate.getMonth() === selectedDate.getMonth() &&
      eventDate.getFullYear() === selectedDate.getFullYear()
    );
  });

  const groupedEvents = groupEvents(dayEvents);

  // Drag-to-select a time range (Notion-style). A plain click resolves to a
  // default one-hour slot, preserving the old click-to-create behaviour.
  const { selection, onColumnPointerDown, minToPx } = useTimeGridSelection({
    firstHour: earliestEventHour,
    totalHours: hours.length,
    hourHeightPx: HOUR_HEIGHT_PX,
    onSelect: (_dayKey, startMin, endMin) => {
      const start = new Date(selectedDate);
      start.setHours(Math.floor(startMin / 60), startMin % 60, 0, 0);
      const end = new Date(selectedDate);
      end.setHours(Math.floor(endMin / 60), endMin % 60, 0, 0);
      onDateCellClick(start, end);
    },
  });

  return (
    <div className="flex">
      <div className="flex flex-1 flex-col">
        <div>
          <DayViewMultiDayEventsRow
            selectedDate={selectedDate}
            multiDayEvents={multiDayEvents}
          />

          {/* Day header */}
          <div className="relative z-20 flex border-b">
            <div className="w-[72px]" />
            <span className="flex-1 border-l py-2 text-center text-xs font-medium text-muted-foreground">
              {format(selectedDate, "EE")}{" "}
              <span className="font-semibold text-foreground">
                {format(selectedDate, "d")}
              </span>
            </span>
          </div>
        </div>

        <ScrollArea className="h-[800px]" type="always">
          <div className="flex">
            {/* Hours column */}
            <div className="relative w-[72px]">
              {hours.map((hour, index) => (
                <div key={hour} className="relative" style={{ height: "96px" }}>
                  <div className="absolute -top-3 right-2 flex h-6 items-center">
                    {index !== 0 && (
                      <span className="text-xs text-muted-foreground">
                        {format(new Date().setHours(hour, 0, 0, 0), "hh a")}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Day grid */}
            <div className="relative flex-1 border-l">
              <div
                className="relative"
                style={{ height: hours.length * HOUR_HEIGHT_PX }}
              >
                {/* Interaction layer: click or drag to sweep out a time range.
                    Sits behind the event blocks (rendered later), so events
                    stay clickable. */}
                <div
                  className="absolute inset-0 cursor-pointer"
                  onMouseDown={(e) => onColumnPointerDown(e, "day")}
                  aria-label="Drag to create an event"
                />

                {/* Hour guide lines + working-hour shading (non-interactive). */}
                {hours.map((hour, index) => {
                  const isDisabled = !isWorkingHour(
                    selectedDate,
                    hour,
                    workingHours,
                  );

                  return (
                    <div
                      key={hour}
                      className={cn(
                        "pointer-events-none relative",
                        isDisabled && "bg-muted/40",
                      )}
                      style={{ height: "96px" }}
                    >
                      {index !== 0 && (
                        <div className="absolute inset-x-0 top-0 border-b" />
                      )}
                      <div className="absolute inset-x-0 top-1/2 border-b border-dashed" />
                    </div>
                  );
                })}

                {/* Live drag-selection highlight. */}
                {selection && selection.endMin > selection.startMin && (
                  <div
                    className="pointer-events-none absolute inset-x-0 z-10 mx-0.5 flex justify-start rounded-md border border-primary/50 bg-primary/15"
                    style={{
                      top: minToPx(selection.startMin),
                      height:
                        minToPx(selection.endMin) - minToPx(selection.startMin),
                    }}
                  >
                    <span className="m-1 rounded bg-primary px-1 text-[10px] font-medium leading-tight text-primary-foreground">
                      {formatMinLabel(selection.startMin)} –{" "}
                      {formatMinLabel(selection.endMin)}
                    </span>
                  </div>
                )}

                {groupedEvents.map((group, groupIndex) =>
                  group.map((event) => {
                    const baseStyle = getEventBlockStyle(
                      event,
                      selectedDate,
                      groupIndex,
                      groupedEvents.length,
                      {
                        from: earliestEventHour,
                        to: latestEventHour,
                      },
                    );
                    const hasOverlap = groupedEvents.some(
                      (otherGroup, otherIndex) =>
                        otherIndex !== groupIndex &&
                        otherGroup.some((otherEvent) =>
                          areIntervalsOverlapping(
                            {
                              start: parseISO(event.startDate),
                              end: parseISO(event.endDate),
                            },
                            {
                              start: parseISO(otherEvent.startDate),
                              end: parseISO(otherEvent.endDate),
                            },
                          ),
                        ),
                    );

                    const style = hasOverlap
                      ? baseStyle
                      : { ...baseStyle, width: "100%", left: "0%" };

                    return (
                      <div
                        key={event.id}
                        className="absolute p-1"
                        style={style}
                      >
                        <EventBlock event={event} />
                      </div>
                    );
                  }),
                )}
              </div>

              <CalendarTimeline
                firstVisibleHour={earliestEventHour}
                lastVisibleHour={latestEventHour}
              />
            </div>
          </div>
        </ScrollArea>
      </div>

      <div className="hidden w-64 divide-y border-l md:block">
        <Calendar
          className="mx-auto w-fit"
          mode="single"
          selected={selectedDate}
          onSelect={(date) => date && setSelectedDate(date)}
        />

        <div className="flex-1 space-y-3">
          {currentEvents.length > 0 ? (
            <div className="flex items-start gap-2 px-4 pt-4">
              <span className="relative mt-[5px] flex size-2.5">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex size-2.5 rounded-full bg-green-600" />
              </span>

              <p className="text-sm font-semibold text-foreground">
                Happening now
              </p>
            </div>
          ) : (
            <p className="p-4 text-center text-sm italic text-muted-foreground">
              No timed events at the moment
            </p>
          )}

          {currentEvents.length > 0 && (
            <ScrollArea className="h-[422px] px-4" type="always">
              <div className="space-y-6 pb-4">
                {currentEvents.map((event) => (
                  <div key={event.id} className="space-y-1.5">
                    <p className="line-clamp-2 text-sm font-semibold">
                      {event.title}
                    </p>

                    {event.meta?.jobName && (
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Briefcase className="size-3.5" />
                        <span className="text-sm">{event.meta.jobName}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <CalendarIcon className="size-3.5" />
                      <span className="text-sm">
                        {format(new Date(), "MMM d, yyyy")}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Clock className="size-3.5" />
                      <span className="text-sm">
                        {format(parseISO(event.startDate), "h:mm a")} -{" "}
                        {format(parseISO(event.endDate), "h:mm a")}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </div>
    </div>
  );
}
